import styles from "./DropMenu.module.scss";
import { classNames } from "../../Utilities";
import { FC, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { createPortal } from "react-dom";

export type DropMenuDirection = "left" | "right";

export interface DropMenuItem {
    key?: string;
    text: ReactNode;
    icon?: IconDefinition;
    disabled?: boolean;
    onClick: () => void;
}

export interface DropMenuProps {
    items: DropMenuItem[];
    direction?: DropMenuDirection;
    className?: string;
    menuClassName?: string;
    disabled?: boolean;
    closeOnItemClick?: boolean;
    trigger: ReactNode;
}

type MenuPosition = {
    top: number;
    left?: number;
    right?: number;
    minWidth?: number;
};

export const DropMenu: FC<DropMenuProps> = (props) => {
    const id = useId();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLSpanElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [open, setOpen] = useState<boolean>(false);
    const [position, setPosition] = useState<MenuPosition | null>(null);

    const direction: DropMenuDirection = props.direction ?? "right";
    const closeOnItemClick = props.closeOnItemClick ?? true;

    const close = () => setOpen(false);
    const toggle = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();

        if (props.disabled) return;
        setOpen(prev => !prev);
    };

    const updatePosition = () => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();

        // Use fixed positioning and viewport coords so we don't get clipped by overflow parents.
        // Keep the menu aligned to the trigger's left/right edge depending on direction.
        const top = rect.bottom + 6;

        if (direction === "right") {
            setPosition({ top, left: rect.left, minWidth: rect.width });
        }
        else {
            const right = window.innerWidth - rect.right;
            setPosition({ top, right, minWidth: rect.width });
        }
    };

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }

        updatePosition();
    }, [open, direction]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent | PointerEvent) => {
            const target = e.target as Node | null;
            if (!target) return;

            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;

            close();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };

        const onReposition = () => updatePosition();

        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);

        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
    }, [open, direction]);

    const handleItemClicked = (item: DropMenuItem) => {
        if (item.disabled) return;
        item.onClick();
        if (closeOnItemClick) close();
    };

    const renderMenu = () => {
        if (!open || !position) return null;

        return (
            <div
                ref={menuRef}
                id={id}
                role="menu"
                className={classNames([
                    styles.menu,
                    direction === "right" ? styles.right : styles.left,
                    props.menuClassName
                ])}
                style={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    right: position.right,
                    minWidth: position.minWidth
                }}
            >
                {props.items.map((item, index) => (
                    <button
                        type="button"
                        key={item.key ?? index}
                        role="menuitem"
                        className={styles.item}
                        disabled={item.disabled}
                        onClick={() => handleItemClicked(item)}
                    >
                        <span className={styles.icon}>
                            {!!item.icon && <FontAwesomeIcon icon={item.icon} />}
                        </span>
                        <span className={styles.text}>{item.text}</span>
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div ref={containerRef} className={classNames([styles.container, props.className])}>
            <span ref={triggerRef} className={styles.trigger} onClick={e => toggle(e)} aria-haspopup="menu" aria-expanded={open}>
                {props.trigger}
            </span>

            {open && position && createPortal(renderMenu(), document.body)}
        </div>
    );
};
